import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key, Plus, Trash2, Copy, CheckCircle, Code, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

export default function APIKeys() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [creating, setCreating] = useState(false);

  const plan = user?.subscription_plan || 'free';
  const hasApiAccess = plan === 'enterprise';

  useEffect(() => {
    if (hasApiAccess) fetchKeys();
    else setLoading(false);
  }, [token, hasApiAccess]);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeys(res.data);
    } catch (error) {
      console.error('Error fetching keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!keyName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/api-keys`,
        { name: keyName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewKey(res.data.api_key);
      setKeyName('');
      setShowForm(false);
      fetchKeys();
      toast.success('Clé API créée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer cette clé ?')) return;
    try {
      await axios.delete(`${API_URL}/api/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clé révoquée');
      fetchKeys();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Clés API</h1>
            <p className="text-zinc-500 text-xs font-mono mt-1 uppercase tracking-widest">Accès programmatique à CyberGuard</p>
          </div>
          {hasApiAccess && (
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle clé
            </Button>
          )}
        </div>

        {/* Pas d'accès */}
        {!hasApiAccess && (
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-sm p-8 text-center space-y-4">
            <CreditCard className="w-12 h-12 text-purple-400 mx-auto" />
            <h3 className="text-white font-mono font-bold text-sm uppercase">Fonctionnalité Enterprise</h3>
            <p className="text-zinc-500 font-mono text-xs leading-relaxed max-w-md mx-auto">
              L'accès à l'API publique est réservé au plan Enterprise. Intégrez CyberGuard directement dans vos outils et automatisez vos audits de sécurité.
            </p>
            <Button onClick={() => navigate('/billing')} className="bg-purple-600 hover:bg-purple-700 font-mono rounded-none text-xs uppercase">
              Passer à Enterprise →
            </Button>
          </div>
        )}

        {/* Nouvelle clé affichée */}
        {newKey && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-6 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="text-white font-mono font-bold text-sm uppercase">Clé créée — copiez-la maintenant !</h3>
            </div>
            <p className="text-zinc-400 font-mono text-xs">Cette clé ne sera plus affichée après fermeture.</p>
            <div className="flex items-center gap-2 bg-black border border-zinc-800 p-3 rounded-sm">
              <code className="flex-1 text-green-400 text-xs font-mono break-all">{newKey}</code>
              <button onClick={() => copyToClipboard(newKey)} className="text-zinc-400 hover:text-white transition-colors shrink-0">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <button onClick={() => setNewKey(null)} className="text-zinc-500 font-mono text-xs uppercase hover:text-white transition-colors">
              J'ai copié ma clé ✓
            </button>
          </div>
        )}

        {/* Formulaire création */}
        {showForm && hasApiAccess && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-4">
            <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-blue-500 pl-3">
              Créer une clé API
            </h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 font-mono text-[10px] uppercase tracking-widest">Nom de la clé</Label>
                <Input
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="Production, Staging, CI/CD..."
                  className="bg-black border-zinc-800 text-white font-mono"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="font-mono text-xs">
                  Annuler
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase" disabled={creating}>
                  {creating ? 'Création...' : 'Créer la clé'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des clés */}
        {hasApiAccess && keys.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm divide-y divide-zinc-800">
            <div className="px-6 py-3 bg-black/20">
              <h2 className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Clés actives</h2>
            </div>
            {keys.map((key) => (
              <div key={key.api_key_id} className={`px-6 py-4 flex items-center justify-between ${!key.is_active ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center border ${key.is_active ? 'bg-blue-500/10 border-blue-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                    <Key className={`w-4 h-4 ${key.is_active ? 'text-blue-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <div className="text-white font-mono text-sm font-bold">{key.name}</div>
                    <div className="text-zinc-500 font-mono text-[10px]">{key.key_preview} · {key.is_active ? 'Active' : 'Révoquée'}</div>
                    {key.last_used && (
                      <div className="text-zinc-600 font-mono text-[10px]">
                        Dernière utilisation : {new Date(key.last_used).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </div>
                </div>
                {key.is_active && (
                  <button onClick={() => handleRevoke(key.api_key_id)} className="text-zinc-600 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Aucune clé */}
        {hasApiAccess && keys.length === 0 && !showForm && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-12 text-center space-y-4">
            <Key className="w-12 h-12 text-zinc-700 mx-auto" />
            <h3 className="text-white font-mono font-bold uppercase">Aucune clé API</h3>
            <p className="text-zinc-500 font-mono text-xs">Créez votre première clé pour accéder à l'API</p>
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase">
              <Plus className="w-4 h-4 mr-2" /> Créer une clé
            </Button>
          </div>
        )}

        {/* Documentation API */}
        {hasApiAccess && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-6">
            <h3 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-blue-500 pl-3 flex items-center gap-2">
              <Code className="w-4 h-4 text-blue-500" /> Documentation API
            </h3>

            <div className="space-y-2">
              <h4 className="text-zinc-300 font-mono text-xs font-bold uppercase">Authentification</h4>
              <p className="text-zinc-500 font-mono text-xs">Ajoutez votre clé dans le header de chaque requête :</p>
              <pre className="bg-black border border-zinc-800 p-3 rounded-sm text-xs text-green-400 font-mono overflow-x-auto">
{`X-API-Key: cg_votre_cle_api`}
              </pre>
            </div>

            <div className="space-y-2">
              <h4 className="text-zinc-300 font-mono text-xs font-bold uppercase">Lancer un scan</h4>
              <pre className="bg-black border border-zinc-800 p-3 rounded-sm text-xs text-blue-400 font-mono overflow-x-auto">
{`POST https://cyberguard-api-ka8f.onrender.com/api/v1/scan
X-API-Key: cg_votre_cle_api
Content-Type: application/json

{
  "target_domain": "example.com",
  "name": "Mon audit"
}`}
              </pre>
            </div>

            <div className="space-y-2">
              <h4 className="text-zinc-300 font-mono text-xs font-bold uppercase">Récupérer les audits</h4>
              <pre className="bg-black border border-zinc-800 p-3 rounded-sm text-xs text-blue-400 font-mono overflow-x-auto">
{`GET https://cyberguard-api-ka8f.onrender.com/api/v1/audits
X-API-Key: cg_votre_cle_api`}
              </pre>
            </div>

            <div className="space-y-2">
              <h4 className="text-zinc-300 font-mono text-xs font-bold uppercase">Détail d'un audit</h4>
              <pre className="bg-black border border-zinc-800 p-3 rounded-sm text-xs text-blue-400 font-mono overflow-x-auto">
{`GET https://cyberguard-api-ka8f.onrender.com/api/v1/audits/{audit_id}
X-API-Key: cg_votre_cle_api`}
              </pre>
            </div>

            <div className="space-y-2">
              <h4 className="text-zinc-300 font-mono text-xs font-bold uppercase">Exemple de réponse</h4>
              <pre className="bg-black border border-zinc-800 p-3 rounded-sm text-xs text-zinc-400 font-mono overflow-x-auto">
{`{
  "audit_id": "audit_abc123",
  "target_domain": "example.com",
  "security_score": 78,
  "vulnerabilities": {
    "high": 1,
    "medium": 2,
    "low": 3
  },
  "scan_details": [...],
  "created_at": "2025-05-01T12:00:00Z"
}`}
              </pre>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
